import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Button,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { AxiosResponse } from 'axios';
import { useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import {
  trackAddPowerListCallOutcomeRule,
  trackClickedPowerListActionDropdown,
  trackClickedPowerListCallOutcomeDropdown,
  trackDeletePowerListCallOutcomeRule,
} from '../../../common/userTracking';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../modals/ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  closeIcon: {
    position: 'absolute',
    top: '10px',
    right: '20px',
  },
  icon: {
    height: 'max-content',
    alignSelf: 'center',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '20vw',
  },
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '170px',
  },
  amount: {
    marginTop: '19.8px', // space between fields
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(1), // space between fields
    width: '65px',
  },
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '65px',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
}));
export const PowerlistDispositionWarningModal = ({
  showPowerlistDispositionWarning,
  handleHidePowerlistDispositionWarning,
  onContinue,
  onGoBack,
}) => {
  return (
    <ModalWithStyles
      title="Warning"
      size="sm"
      show={showPowerlistDispositionWarning}
      onHide={handleHidePowerlistDispositionWarning}
      submitLabel="Continue"
      cancelLabel="Go Back"
      footerComponent={
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
          <div>
            <Button
              variant="contained"
              color="inherit"
              disableElevation={true}
              onClick={onGoBack}
            >
              Go Back
            </Button>
          </div>
          <div>
            <Button
              color="primary"
              variant="contained"
              disableElevation={true}
              onClick={onContinue}
            >
              Continue
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ paddingTop: '20px' }}>
        <p>
          Any changes you’ve made to the Call Outcome Rules for this PowerList
          will be replaced with the Default Call Outcome Rules for this account.
          Toggling default call outcome rules OFF will restore your custom
          configuration. Are you sure you want to continue?
        </p>
      </div>
    </ModalWithStyles>
  );
};
// Cadence - Dispositions Tab
export function Dispositions({
  filteredDispoList,
  dispoList,
  formik,
  powerlistId,
  isAgentPowerlist,
}) {
  const classes = useStyles();
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [
    showPowerlistDispositionWarning,
    setShowPowerlistWarningModal,
  ] = useState(false);
  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtomContents?.businessid}`,
    })
  );
  const cadences = useMemo(
    () =>
      cadenceReportLoadable.state === 'hasValue'
        ? [
            ...(cadenceReportLoadable.contents?.response?.data ?? []),
          ]?.sort((a, b) =>
            a?.cadenceName
              ?.toLowerCase()
              ?.localeCompare(b?.cadenceName?.toLowerCase())
          ) ?? []
        : [],
    [cadenceReportLoadable]
  );
  const data = {
    call: methodNameEnum.GET_SMS_TEMPLATE,
  };
  const smsTemplates = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_SMS_TEMPLATE,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const smsTemplateOptions = useMemo(
    () =>
      smsTemplates.state === 'hasValue'
        ? [...(smsTemplates.contents?.response?.data?.result ?? [])]
            ?.filter((template) => template.hasCustomField === 0)
            .sort((a, b) =>
              a.smsTemplateName
                ?.toLocaleLowerCase()
                ?.localeCompare(b?.smsTemplateName?.toLocaleLowerCase())
            )
        : [],
    [smsTemplates]
  );

  const thisAgentPLBoolean =
    isAgentPowerlist !== undefined ||
    (!powerlistId &&
      (window.location.pathname.includes('manage/agents') ||
        window.location.pathname.includes('my-profile')));

  const powerlistReportLoadable =
    //if we are editing an agent powerlist, or adding a new powerlist within the agent powerlists page
    useRecoilValueLoadable(
      thisAgentPLBoolean
        ? //get the agent specific powerlists
          makeAPIRequest({
            method: 'get',
            domain: process.env.VITE_APP_CDK_DOMAIN,
            endpoint: `${endpointEnum.GET_USER_TEAM_POWERLISTS}/${
              window.location.pathname.match(
                /[/manage/agents/|/my\-profile/](\d{1,})[/powerlists/]/
              )?.[1]
            }`,
          })
        : makeAPIRequest({
            endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
            method: 'POST',
            payloadJsonString: JSON.stringify({
              call: 'v1/getBusinessPowerlists',
            }),
          })
    );

  const powerlists = useMemo(
    () =>
      thisAgentPLBoolean
        ? //parse agent powerlists data payload
          (powerlistReportLoadable?.contents?.response as AxiosResponse)
            ?.data ??
          powerlistReportLoadable?.contents?.respowerlists ??
          []
        : //else parse business powerlists data payload
          (powerlistReportLoadable?.contents?.response as AxiosResponse)?.data
            ?.powerlists ??
          powerlistReportLoadable?.contents?.respowerlists ??
          [],
    [powerlistReportLoadable]
  );

  const filterOptions = (index: number) => {
    const currDispoId =
      formik.values.powerlistDispositionRules?.[index]?.businessDispositionId;
    let firstIndex: number | undefined;
    for (let i = 0; i < formik.values.powerlistDispositionRules.length; i++) {
      const rule = formik.values.powerlistDispositionRules[i];

      if (
        Number(rule.businessDispositionId) === Number(currDispoId) &&
        rule.action === 'remove'
      ) {
        if (!firstIndex) {
          firstIndex = i;
        }
      }
    }
    const firstStop = index === firstIndex;

    if (firstIndex === undefined || firstStop) {
      return [
        { value: 'sendSMS', text: 'Send SMS Template' },
        { value: 'remove', text: 'Remove From PowerList' },
        { value: 'forwardToCadence', text: 'Forward To Cadence' },
        { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
        { value: 'assignOwner', text: 'Assign Owner' },
      ];
    } else {
      return [
        { value: 'sendSMS', text: 'Send SMS Template' },
        { value: 'forwardToCadence', text: 'Forward To Cadence' },
        { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
        { value: 'assignOwner', text: 'Assign Owner' },
      ];
    }
  };
  const filterPowerlists = (index: number) => {
    const correspondingFPLid =
      formik.values.powerlistDispositionRules[index]?.forwardToPowerlistId;
    const dispoId =
      formik.values.powerlistDispositionRules[index]?.businessDispositionId;
    const arrOfSelectedPowerlistIds: any[] = [];
    for (let i = 0; i < formik.values.powerlistDispositionRules?.length; i++) {
      const r = formik.values.powerlistDispositionRules[i];
      if (
        r.action !== 'remove' &&
        r.forwardToPowerlistId &&
        r.businessDispositionId == dispoId
      ) {
        arrOfSelectedPowerlistIds.push(r.forwardToPowerlistId);
      }
    }
    return powerlists
      ? powerlists
          ?.filter(
            (powerlist) =>
              (thisAgentPLBoolean //if this is an existing agent powerlist
                ? powerlist.isAgentPowerlist === 1
                : powerlist.isAgentPowerlist !== 1) &&
              powerlist.nextPowerlistId !== parseInt(powerlistId) &&
              powerlist.powerlistId !== parseInt(powerlistId)
          )
          ?.filter(
            (powerlist) =>
              powerlist.id === correspondingFPLid ||
              !arrOfSelectedPowerlistIds.includes(powerlist.id)
          )
          ?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
      : [];
  };

  const filterCadences = (index: number) => {
    const correspondingCDid =
      formik.values.powerlistDispositionRules[index]?.forwardToCadenceId;
    const dispoId =
      formik.values.powerlistDispositionRules[index]?.businessDispositionId;
    const arrOfSelectedCadenceIds: any[] = [];
    for (let i = 0; i < formik.values.powerlistDispositionRules?.length; i++) {
      const r = formik.values.powerlistDispositionRules[i];
      if (
        r.action !== 'remove' &&
        r.businessDispositionId == dispoId &&
        r.forwardToCadenceId
      ) {
        arrOfSelectedCadenceIds.push(r.forwardToCadenceId);
      }
    }

    return cadences
      ? cadences
          ?.filter(
            (cadence) =>
              cadence.cadenceId === correspondingCDid ||
              !arrOfSelectedCadenceIds.includes(cadence.cadenceId)
          )
          ?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
      : [];
  };
  const filterSMSTemplates = (index: number) => {
    const correspondingTempladeId =
      formik.values.powerlistDispositionRules[index]?.smsTemplateId;
    const dispoId =
      formik.values.powerlistDispositionRules[index]?.businessDispositionId;
    const arrOfSelectedTemplateIds: any[] = [];
    for (let i = 0; i < formik.values.powerlistDispositionRules?.length; i++) {
      const r = formik.values.powerlistDispositionRules[i];
      if (
        r.action !== 'remove' &&
        r.smsTemplateId &&
        r.businessDispositionId == dispoId
      ) {
        arrOfSelectedTemplateIds.push(r.smsTemplateId);
      }
    }

    return smsTemplateOptions
      ? smsTemplateOptions
          ?.filter(
            (template) =>
              template.smsTemplateid === correspondingTempladeId ||
              !arrOfSelectedTemplateIds.includes(template.smsTemplateid)
          )
          ?.sort((a, b) =>
            a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
          )
      : [];
  };

  return (
    <div className="container">
      <PowerlistDispositionWarningModal
        showPowerlistDispositionWarning={showPowerlistDispositionWarning}
        handleHidePowerlistDispositionWarning={() => {
          setShowPowerlistWarningModal(false);
        }}
        onContinue={() => {
          formik.setFieldValue('useDefaultDispositionRules', true);
          setShowPowerlistWarningModal(false);
        }}
        onGoBack={() => {
          formik.setFieldValue('useDefaultDispositionRules', false);
          setShowPowerlistWarningModal(false);
        }}
      />
      {process.env.VITE_APP_BUSINESS_POWERLIST_DISPOSITION_RULES === '1' && (
        <div className="row">
          <div className="col-lg-11" style={{ marginLeft: '8px' }}>
            <FormControlLabel
              control={
                <FormikSwitch
                  name="useDefaultDispositionRules"
                  formik={formik}
                  color="primary"
                  type="checkbox"
                  handleChange={(e) => {
                    !formik.values.useDefaultDispositionRules
                      ? setShowPowerlistWarningModal(true)
                      : formik.setFieldValue(
                          'useDefaultDispositionRules',
                          e.target.checked
                        );
                  }}
                />
              }
              label="Use Default Call Outcome Rules"
            />
          </div>
          <div className="col-lg-11" style={{ margin: '8px 8px 16px 8px' }}>
            {!formik?.values?.useDefaultDispositionRules && (
              <Link
                onClick={() => {
                  formik.setFieldValue('powerlistDispositionRules', [
                    {
                      businessDispositionId: '',
                      action: '',
                      status: 'active',
                      forwardToCadenceId: '',
                      forwardToPowerlistId: '',
                      smsTemplateId: '',
                    } as any,
                  ]);
                }}
              >
                Clear All Call Outcome Rules
              </Link>
            )}
          </div>
        </div>
      )}
      {formik.values.powerlistDispositionRules.map((dispo, index) => (
        <div
          key={index}
          className="row"
          style={{ flexWrap: 'inherit', marginTop: '13.5px' }}
        >
          <div className="col-11">
            <LabelledOutline
              key={index}
              label={`Call Outcome Rule ${index + 1}`}
            >
              <FormikSelect
                name={`powerlistDispositionRules.${index}.businessDispositionId`}
                className={classes.fieldInsideRow}
                formik={formik}
                label="Call Outcome"
                size="small"
                variant="outlined"
                disabled={formik.values.useDefaultDispositionRules}
                onClick={() => {
                  trackClickedPowerListCallOutcomeDropdown();
                }}
              >
                {!!formik.values.powerlistDispositionRules[index]
                  .businessDispositionId && (
                  <MenuItem
                    value={
                      Number(
                        formik.values.powerlistDispositionRules[index]
                          ?.businessDispositionId
                      ) ?? ''
                    }
                  >
                    {dispoList.find(
                      (d) =>
                        Number(
                          formik.values.powerlistDispositionRules[index]
                            .businessDispositionId
                        ) === Number(d.businessDispositionid)
                    )?.disposition ?? ''}
                  </MenuItem>
                )}
                {filteredDispoList?.map((dispo, index) => (
                  <MenuItem key={index} value={dispo.businessDispositionid}>
                    {dispo.disposition}
                  </MenuItem>
                ))}
              </FormikSelect>
              <FormikSelect
                name={`powerlistDispositionRules.${index}.action`}
                className={classes.fieldInsideRow}
                formik={formik}
                label="Action"
                size="small"
                variant="outlined"
                disabled={formik.values.useDefaultDispositionRules}
                onClick={() => {
                  trackClickedPowerListActionDropdown();
                }}
              >
                {filterOptions(index).map((option, index) => (
                  <MenuItem value={option.value} key={index}>
                    {option.text}
                  </MenuItem>
                ))}
              </FormikSelect>
              {formik.values.powerlistDispositionRules[index].action ===
                'forwardToCadence' && (
                <FormikSelect
                  name={`powerlistDispositionRules.${index}.forwardToCadenceId`}
                  className={classes.fieldInsideRow}
                  formik={formik}
                  label="Cadence"
                  size="small"
                  variant="outlined"
                  disabled={formik.values.useDefaultDispositionRules}
                >
                  {filterCadences(index).map((cadence, index) => (
                    <MenuItem key={index} value={cadence.cadenceId}>
                      {cadence.cadenceName}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
              {formik.values.powerlistDispositionRules[index].action ===
                'forwardToPowerlist' && (
                <FormikSelect
                  name={`powerlistDispositionRules.${index}.forwardToPowerlistId`}
                  className={classes.fieldInsideRow}
                  formik={formik}
                  label="PowerList"
                  size="small"
                  variant="outlined"
                  disabled={formik.values.useDefaultDispositionRules}
                >
                  {filterPowerlists(index)?.map((powerlist, index) => (
                    <MenuItem key={index} value={powerlist.powerlistId}>
                      {powerlist.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
              {formik.values.powerlistDispositionRules[index].action ===
                'sendSMS' && (
                <FormikSelect
                  name={`powerlistDispositionRules.${index}.smsTemplateId`}
                  className={classes.fieldInsideRow}
                  formik={formik}
                  label="SMS Template Name"
                  size="small"
                  variant="outlined"
                  required={
                    formik.values.powerlistDispositionRules[index].action ===
                    'sendSMS'
                  }
                  disabled={formik.values.useDefaultDispositionRules}
                >
                  {filterSMSTemplates(index).map((smsTemplate, index) => (
                    <MenuItem key={index} value={smsTemplate.smsTemplateid}>
                      {smsTemplate.smsTemplateName}
                    </MenuItem>
                  ))}
                </FormikSelect>
              )}
            </LabelledOutline>
          </div>
          <div
            className="col-1"
            style={{
              paddingLeft: '0px',
              display: 'flex',
              flexWrap: 'nowrap',
            }}
          >
            <Tooltip title="add">
              <span>
                <IconButton
                  style={{ marginTop: '8px' }}
                  aria-label="add"
                  color="primary"
                  className={classes.icon}
                  disabled={
                    filteredDispoList?.length === 0 ||
                    formik.values.useDefaultDispositionRules
                  }
                  onClick={() => {
                    trackAddPowerListCallOutcomeRule();
                    formik.setFieldValue('powerlistDispositionRules', [
                      ...formik.values.powerlistDispositionRules.slice(0),
                      {
                        businessDispositionId: '',
                        action: '',
                        forwardToCadenceId: '',
                        status: 'active',
                      },
                    ]);
                  }}
                  size="large"
                >
                  <PlaylistAddIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip key={index} title="remove">
              <span>
                <IconButton
                  className={classes.deleteIconButton}
                  style={{ marginTop: '8px' }}
                  aria-label="cancel"
                  disabled={formik.values.useDefaultDispositionRules}
                  onClick={() => {
                    trackDeletePowerListCallOutcomeRule();
                    formik.setFieldValue(
                      'powerlistDispositionRules',
                      formik.values.powerlistDispositionRules.length > 1
                        ? [
                            ...formik.values.powerlistDispositionRules.slice(
                              0,
                              index
                            ),
                            ...formik.values.powerlistDispositionRules.slice(
                              index + 1
                            ),
                          ]
                        : [
                            {
                              businessDispositionId: '',
                              action: '',
                              forwardToCadenceId: '',
                              status: '',
                            },
                          ]
                    );
                  }}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      ))}
      <div className="row">
        <div className="col-lg-11">
          <LabelledOutline label={'Max SMS sent per Template'}>
            <div className="row">
              <FormikTextField
                formik={formik}
                className={classes.amount}
                name={`maxSmsPerTemplateCount`}
                label="Amount"
                margin="dense"
                size="small"
                InputProps={{
                  inputProps: {
                    min: 1,
                    max: 9999,
                  },
                }}
                type="number"
                variant="outlined"
              />
              <span style={{ marginTop: '13px' }} className="timeframe">
                <LabelledOutline label="Per" customPadding="5px">
                  <FormikTextField
                    className={classes.timeframes}
                    formik={formik}
                    name={`maxSmsPerTemplateDurationInDaySub`}
                    label="Days"
                    margin="dense"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 90,
                      },
                    }}
                    type="number"
                    variant="outlined"
                  />
                  <FormikTextField
                    className={classes.timeframes}
                    formik={formik}
                    name={`maxSmsPerTemplateDurationInHourSub`}
                    label="Hours"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 23,
                      },
                    }}
                    margin="dense"
                    type="number"
                    variant="outlined"
                  />
                  <FormikTextField
                    className={classes.timeframes}
                    formik={formik}
                    name={`maxSmsPerTemplateDurationInMinSub`}
                    label="Minutes"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 59,
                      },
                    }}
                    margin="dense"
                    type="number"
                    variant="outlined"
                  />
                </LabelledOutline>
              </span>
            </div>
          </LabelledOutline>
        </div>
      </div>
    </div>
  );
}
